/* eslint-disable no-nested-ternary */
import React, { memo, useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AiColumnsProps } from './type';

import { AiColEnrichmentModalSteps } from './enums';
import AI_COLUMNS_STEP_COMPONENT_MAP from './components/ai-col-steps-list';
import Modal from '../../../../shared/design-system/ui/modal/modal';
import {
  resetAutoRunConditionFilterGroups,
  resetGetTemplateById,
  setAiColumnModalVisible,
  setAiEnrichmentOpenContext,
  setAutoRunConditionFilterGroupsLogic,
  setIsAiColumnTemplateListCachedFromFilter,
} from '../../prospect-slice';
import { getProspectFiltersFieldsListRequest } from '../../extra-actions';
import { RootState } from '../../../../store/root-reducer';

const AiColumns: React.FC<AiColumnsProps> = memo(
  ({ show, onClose, initialStep, initialStepData }) => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const urlParams = new URLSearchParams(location?.search || '');
    const templateId = urlParams.get('templateId');
    const viewId = urlParams.get('viewId');

    const [currentStep, setCurrentStep] = useState<AiColEnrichmentModalSteps>(
      initialStep || AiColEnrichmentModalSteps.ENRICH_OPTION,
    );
    const [stepData, setStepData] = useState<any>(initialStepData || {});
    const [showIntro, setShowIntro] = useState(true);

    const [isVisible, setIsVisible] = useState(show);
    const [isClosing, setIsClosing] = useState(false);
    const [previousStep, setPreviousStep] = useState<AiColEnrichmentModalSteps>(
      null,
    );

    const prospectFiltersFieldsList = useSelector(
      (state: RootState) => state.prospect.prospectFiltersFieldsList,
    );
    const currentViewType = useSelector(
      (state: RootState) => state?.prospect?.currentViewType,
    );

    useEffect(() => {
      if (show) {
        setIsVisible(true);
        setIsClosing(false);
      } else if (isVisible) {
        setIsClosing(true);
        const timer = setTimeout(() => {
          setIsVisible(false);
          setIsClosing(false);
        }, 400); // match animation-duration
        return () => clearTimeout(timer);
      }
    }, [show]);

    useEffect(() => {
      if (initialStep && initialStep !== currentStep) {
        setCurrentStep(initialStep);
      }
      if (initialStepData && Object.keys(initialStepData).length > 0) {
        setStepData(initialStepData);
      }
    }, [initialStep, initialStepData]);

    // Reset intro when modal opens
    useEffect(() => {
      if (show) {
        setShowIntro(true);
        if (prospectFiltersFieldsList.length === 0) {
          dispatch(getProspectFiltersFieldsListRequest());
        }
        dispatch(resetAutoRunConditionFilterGroups());
        dispatch(setAutoRunConditionFilterGroupsLogic('AND'));
      }
    }, [show]);

    const StepComponent = useMemo(
      () => AI_COLUMNS_STEP_COMPONENT_MAP?.[currentStep] || null,
      [currentStep],
    );

    const handleMoveToNextStep = (
      nextStep: AiColEnrichmentModalSteps,
      data?: any,
    ) => {
      // Track source step when navigating to TEMPLATE_LIST
      setPreviousStep(currentStep);
      if (nextStep === AiColEnrichmentModalSteps.TEMPLATE_LIST) {
        setStepData((prev) => ({
          ...prev,
          [nextStep]: data,
          sourceStep: currentStep, // Track where we came from
        }));
      } else if (data) {
          setStepData((prev) => ({ ...prev, [nextStep]: data }));
        }
      setCurrentStep(nextStep);

      if (nextStep === AiColEnrichmentModalSteps.EXISTING_COLUMN) {
        history.replace(viewId ? `/crm/list?viewId=${viewId}` : `/crm/list`);
      }

      if (
        nextStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL &&
        data?.shareableId
      ) {
        history.replace(
          viewId
            ? `/crm/list?viewId=${viewId}&templateId=${data.shareableId}`
            : `/crm/list?templateId=${data.shareableId}`,
        );
      }
    };

    const handleModalClose = () => {
      setCurrentStep(AiColEnrichmentModalSteps.ENRICH_OPTION);
      setPreviousStep(null);
      setStepData({});
      dispatch(setAiEnrichmentOpenContext(null));
      dispatch(setAiColumnModalVisible(false));
      dispatch(setIsAiColumnTemplateListCachedFromFilter(true));
      dispatch(resetAutoRunConditionFilterGroups());
      dispatch(setAutoRunConditionFilterGroupsLogic('AND'));
      if (templateId) {
        if (currentViewType === 'kanban') {
          const params = new URLSearchParams(location.search);
          params.delete('templateId');
          history.push({
            search: params.toString() ? `?${params.toString()}` : '',
          });
        } else {
          history.push({
            search: '',
          });
        }
      }
      // history.replace('/crm/list');
      onClose();
    };

    const handleBack = () => {
      // Special handling for TEMPLATE_LIST when coming from SMART_FILL
      if (
        currentStep === AiColEnrichmentModalSteps.TEMPLATE_LIST &&
        stepData?.sourceStep === AiColEnrichmentModalSteps.EXISTING_COLUMN
      ) {
        setPreviousStep(currentStep);
        setCurrentStep(AiColEnrichmentModalSteps.EXISTING_COLUMN);
        return;
      }

      const currentStepMapToPreviousStep = {
        [AiColEnrichmentModalSteps.TEMPLATE_LIST]:
          AiColEnrichmentModalSteps.ENRICH_OPTION,
        [AiColEnrichmentModalSteps.TEMPLATE_DETAIL]:
          AiColEnrichmentModalSteps.TEMPLATE_LIST,
        [AiColEnrichmentModalSteps.CUSTOM_PROMPT]:
          AiColEnrichmentModalSteps.ENRICH_OPTION,
        [AiColEnrichmentModalSteps.EXISTING_COLUMN]:
          AiColEnrichmentModalSteps.ENRICH_OPTION,
      };

      setPreviousStep(currentStep);
      const gotoStep = currentStepMapToPreviousStep[currentStep];
      if (gotoStep) {
        setCurrentStep(gotoStep);
        dispatch(resetAutoRunConditionFilterGroups());
        dispatch(setAutoRunConditionFilterGroupsLogic('AND'));
        // Reset SMART_FILL stepData when navigating back from SMART_FILL
        if (currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN) {
          setStepData((prev) => {
            const {
              [AiColEnrichmentModalSteps.EXISTING_COLUMN]: existingColumnData,
              ...rest
            } = prev;
            return rest;
          });
          // Clear template data from Redux store
          dispatch(resetGetTemplateById());
          // Clear redux open context
          dispatch(setAiEnrichmentOpenContext(null));
          if (currentViewType === 'kanban') {
            history.replace(
              viewId
                ? `/crm/kanban?viewId=${viewId}`
                : `/crm/kanban`,
            );
          } else {
            history.replace('/crm/list');
          }
        }

        if (currentStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL) {
          if (currentViewType === 'kanban') {
            history.replace(
              viewId
                ? `/crm/kanban?viewId=${viewId}`
                : `/crm/kanban`,
            );
          } else {
            history.replace('/crm/list');
          }
        }
      }
    };
    return (
      <Modal
        show={isVisible}
        aria-labelledby="contained-modal-title-vcenter"
        className={`ai-column-drawer-modal ${
          isClosing ? 'closing' : show ? 'opening' : ''
        }`}
        title={undefined}
        onHide={undefined}
        hideFooter={true}
        hideHeader={false}
        backdrop={false}
      >
        {StepComponent && (
          <StepComponent
            handleMoveToNextStep={handleMoveToNextStep}
            handleModalClose={handleModalClose}
            handleBack={handleBack}
            stepData={stepData}
            setStepData={setStepData}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            show={show}
            showIntro={showIntro}
            setShowIntro={setShowIntro}
            previousStep={previousStep}
          />
        )}
      </Modal>
    );
  },
);
export default AiColumns;
